import React, {useState} from "react";
import {Button} from "reactstrap";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import $ from "jquery";
import {ChangeDateFormat} from "../../../Common/Helpers/ChangeDateFormat";
import {ToCustomDecimal} from "../../../AppSystem/Helpers/ToCustomDecimal";
import {CheckUserRole} from "../../../AppSystem/Helpers/CheckUserRole";
import {CheckUserPermission} from "../../../AppSystem/Helpers/CheckUserPermission";
import {CheckMobile} from "../../../AppSystem/Helpers/CheckMobile";
import {CustomModal} from "../../../Common/Components/Modal/CustomModal";
import {CustomButton} from "../../../Common/Components/Button/CustomButton";
import {CustomInput} from "../../../Common/Components/Input/CustomInput";
import {CardCreditInvoiceInfo} from "../../../Invoice/Components/CardCreditInvoiceInfo";
import AdminRequestAttestDenyModel from "../../Model/Request/AdminRequestAttestDenyModel";
import AdminRequestAttestApprovedModel from "../../Model/Request/AdminRequestAttestApprovedModel";
import {RelatedProceedings} from "../RelatedProceedings";
import {CollapsibleSection} from "../../../Common/Components/Detail/CollapsibleSection";
import {Field, FieldBlock} from "../../../Common/Components/Detail/FieldBlock";
import {AttestReimbursementSections} from "./AttestReimbursementSections";
import {AttestHonorariumSections} from "./AttestHonorariumSections";
import {AttestPaySections} from "./AttestPaySections";
import {AttestLogSection} from "./AttestLogSection";
import {GetAttestAmounts} from "../../Helpers/AttestAmounts";

const kr = (value) => `${ToCustomDecimal(value ?? 0)} kr`;

export function AttestDetailView({
                                     data,
                                     relatedData,
                                     onChange,
                                     onChangeApproveComment,
                                     onValidate,
                                     onChangeDenyId,
                                     onchangeDenyReason,
                                     errorReason,
                                     setErrorReason,
                                     setShowConfirmModalApprove,
                                     showConfirmModalApprove,
                                     setShowConfirmModalDeny,
                                     showConfirmModalDeny,
                                     onPageChange,
                                     page,
                                     setPage,
                                     sortConfig,
                                     onSort,
                                     isLoading,
                                 }) {
    const {t} = useTranslation();
    const {rolesData, userData} = useSelector(state => state.auth);
    const isSuperAdmin = CheckUserRole(rolesData, 'SuperAdmin');
    const isSupport = CheckUserRole(rolesData, 'Support');

    const [showConfirmModalWarningApprove, setShowConfirmModalWarningApprove] = useState(false);
    const [showConfirmModalWarningDeny, setShowConfirmModalWarningDeny] = useState(false);

    // On a phone the heading of the case is collapsed, so the sections start
    // right below it instead of a screen further down.
    const isMobile = CheckMobile(998);
    const [isMetaOpen, setIsMetaOpen] = useState(false);

    const modelInfo = data?.modelInfo ?? {};
    const {orgCost, employerFee, payout} = GetAttestAmounts(data);

    const permissionUpdate = CheckUserPermission("admin") || CheckUserPermission("admin.attest") || CheckUserPermission("admin.attest.approve");
    const permissionDeny = CheckUserPermission("admin") || CheckUserPermission("admin.attest") || CheckUserPermission("admin.attest.deny");

    // Blocks the client asked to drop for now. They are kept behind these flags so
    // they can be brought back without rebuilding them.
    const showHandledByInfo = false;
    const showFooterBar = false;

    const isPending = data?.status === "Skickat";
    const isOwnProceeding = data?.createdBy === userData?.id;
    const canApprove = !isOwnProceeding && isPending && permissionUpdate;
    const canDeny = !isOwnProceeding && isPending && permissionDeny;
    const needsLocalGroupWarning = data?.orgType === 'Lokal grupp' && isSupport;

    const openNewTab = (id) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('id', id);
        window.open(currentUrl.toString(), '_blank');
    };

    // Same button locking as the previous layout: the ids are cleared again by
    // AdminAttestInfo once the call succeeds or fails.
    const lockActionButtons = () => {
        $("#btnApproveAttest").addClass("disabled");
        $(`#${data.id}`).addClass("disabled");
        $(".att-action-btn").addClass("disabled");
    };

    const unlockActionButtons = () => {
        $("#btnApproveAttest").removeClass("disabled");
        $(`#${data.id}`).removeClass("disabled");
        $(".att-action-btn").removeClass("disabled");
    };

    const handleApproveClick = () => {
        if (needsLocalGroupWarning) {
            setShowConfirmModalWarningApprove(true);
        } else {
            setShowConfirmModalApprove(true);
        }
        onChange();
        lockActionButtons();
    };

    const handleDenyClick = () => {
        onChangeDenyId(data.id);
        if (needsLocalGroupWarning) {
            setShowConfirmModalWarningDeny(true);
        } else {
            setShowConfirmModalDeny(true);
        }
        lockActionButtons();
    };

    // TITLE / STATUS
    const modelVisualId = data?.modelVisualId || modelInfo?.visualId || '';
    const modelTitle = () => {
        if (data?.model === "Förrättning") return t('reimbursement.reportProcessing');
        if (data?.model === "Arvode") return t('honorarium.honorarium');
        if (data?.model === "Betala") return t('payReceive.pay');
        if (data?.model === "Faktura") return t('invoice.invoice');
        return t('attest.labelInfo');
    };

    const statusBadge = () => {
        if (data?.status === "Nekad") return {text: t('attest.denied'), className: 'att-status-denied'};
        if (data?.status === "Godkänd") return {text: t('attest.approved'), className: 'att-status-approved'};
        if (data?.status === "Utbetald") return {text: t('modelLabel.paidOut'), className: 'att-status-approved'};
        return {text: t('attest.waitingForAttest'), className: 'att-status-pending'};
    };
    const badge = statusBadge();

    const recipientName = modelInfo?.userName || data?.recipient || data?.createdByName;
    const headingTitle = [`${modelTitle()} ${modelVisualId}`.trim(), recipientName].filter(Boolean).join(' - ');

    // What the case is about, written once and laid out differently on a phone.
    // The purpose is not here: it leads the card, right under the heading.
    const metaFields = [
        {label: t('attest.organisation'), value: data?.orgName},
        {label: t('attest.submittedAt'), value: ChangeDateFormat('dateTime', data?.createdAt)},
        modelInfo?.date ? {label: t('attest.activityDate'), value: ChangeDateFormat('date', modelInfo.date)} : null,
        modelInfo?.costBearerName ? {label: t('attest.activityArea'), value: modelInfo.costBearerName} : null,
        data?.costPlace ? {label: t('modelLabel.KST'), value: data.costPlace} : null,
    ].filter(Boolean);

    // MODALS
    // Optional: the approval goes through with an empty comment.
    const approveCommentInput = (
        <div className="d-flex justify-content-center mb-3">
            <div className="w-75 position-relative">
                <CustomInput model={AdminRequestAttestApprovedModel} attribute="comment" customChange={e => onChangeApproveComment && onChangeApproveComment(e)}/>
            </div>
        </div>
    );

    const contentBodyApproved = (
        <>
            {approveCommentInput}
            <div>{t('validationMessage.modalAttest')}</div>
        </>
    );

    const contentFooterApproved = (
        <>
            <Button className="box-green border-0" onClick={async () => {
                setShowConfirmModalApprove(false);
                await onValidate("approve");
            }}>{t('button.accept')}</Button>
            {' '}
            <Button className="box-red border-0" onClick={() => {
                setShowConfirmModalApprove(false);
                unlockActionButtons();
            }}>{t('button.cancel')}</Button>
        </>
    );

    const denyReasonInput = (
        <div className="d-flex justify-content-center">
            <div className="w-75 position-relative">
                <CustomInput model={AdminRequestAttestDenyModel} attribute="reason" customChange={e => onchangeDenyReason(e)}/>
                <div className={`error tooltip-custom-normal ${errorReason === true ? 'opacity-1' : 'opacity-0'}`}
                     style={{color: '#f56e6e'}}>
                    {t('validationMessage.fieldRequired')}
                </div>
            </div>
        </div>
    );

    const contentBodyDeny = (
        <>
            {denyReasonInput}
            <div>{t('validationMessage.modalAttestDeny')}</div>
        </>
    );

    const contentHeaderDeny = (
        <h5 className="mb-0">{t('validationMessage.modalAttestDenyHeader')}</h5>
    );

    const contentFooterDeny = (
        <>
            <Button className="box-green border-0" onClick={async () => {
                setShowConfirmModalApprove(false);
                await onValidate("deny");
            }}>{t('button.accept')}</Button>
            {' '}
            <Button className="box-red border-0" onClick={() => {
                setShowConfirmModalDeny(false);
                setErrorReason(false);
                unlockActionButtons();
            }}>{t('button.cancel')}</Button>
        </>
    );

    const contentHeaderWarning = (
        <h2 className="rokkitt-font avatar-title mb-0">{t('attest.warningTitleLokalGroup')}</h2>
    );

    const contentBodyWarningApproved = (
        <>
            <div style={{marginBottom: '1rem'}}>{t('attest.warningLokalGroup')}</div>
            {approveCommentInput}
            <div>{t('attest.warningLokalGroupSubText')}</div>
        </>
    );

    const contentBodyWarningDeny = (
        <>
            <div style={{marginBottom: '1rem'}}>{t('attest.warningLokalGroup')}</div>
            {denyReasonInput}
            <div>{t('attest.warningLokalGroupSubText')}</div>
        </>
    );

    const contentFooterWarningApproved = (
        <>
            <Button className="box-green border-0" onClick={async () => {
                setShowConfirmModalWarningApprove(false);
                await onValidate("approve");
            }}>{t('button.accept')}</Button>
            <Button className="box-red border-0" onClick={() => {
                setShowConfirmModalWarningApprove(false);
                unlockActionButtons();
            }}>{t('button.cancel')}</Button>
        </>
    );

    const contentFooterWarningDeny = (
        <>
            <Button className="box-green border-0" onClick={async () => {
                setShowConfirmModalWarningDeny(false);
                await onValidate("deny");
            }}>{t('button.accept')}</Button>
            <Button className="box-red border-0" onClick={() => {
                setShowConfirmModalWarningDeny(false);
                setErrorReason(false);
                unlockActionButtons();
            }}>{t('button.cancel')}</Button>
        </>
    );

    // Same CustomButton treatment as the list's create button (shape, sizes, hover).
    const actionButtons = (idPrefix) => (
        <>
            {canDeny ?
                <CustomButton
                    id={idPrefix === 'top' ? data.id : undefined}
                    text={t('attest.deny')}
                    buttonColor="#FFFFFF"
                    hoverButtonColor="#CDE0DA"
                    textColor="#157553"
                    hoverTextColor="#084F35"
                    buttonSize="big"
                    customClasses="att-btn-deny att-action-btn"
                    onClick={handleDenyClick}
                />
            : ''}
            {canApprove ?
                <CustomButton
                    id={idPrefix === 'top' ? 'btnApproveAttest' : undefined}
                    text={t('attest.approve')}
                    icon="bx bx-check-circle pe-2"
                    buttonColor="#084F35"
                    hoverButtonColor="#157553"
                    buttonSize="big"
                    customClasses="att-btn-approve att-action-btn"
                    onClick={handleApproveClick}
                />
            : ''}
        </>
    );

    return (
        <div className="att-detail">
            <CustomModal isOpen={showConfirmModalApprove} toggle={() => setShowConfirmModalApprove(!showConfirmModalApprove)} body={contentBodyApproved} footer={contentFooterApproved}/>
            <CustomModal isOpen={showConfirmModalDeny} toggle={() => setShowConfirmModalDeny(!showConfirmModalDeny)} header={contentHeaderDeny} body={contentBodyDeny} footer={contentFooterDeny}/>
            <CustomModal isOpen={showConfirmModalWarningApprove} toggle={() => setShowConfirmModalWarningApprove(!showConfirmModalWarningApprove)} header={contentHeaderWarning} body={contentBodyWarningApproved} footer={contentFooterWarningApproved}/>
            <CustomModal isOpen={showConfirmModalWarningDeny} toggle={() => setShowConfirmModalWarningDeny(!showConfirmModalWarningDeny)} header={contentHeaderWarning} body={contentBodyWarningDeny} footer={contentFooterWarningDeny}/>

            <div className="att-top">
                <div className="att-top-main att-card">
                    <div
                        className={`att-top-head ${isMobile ? 'att-top-head-clickable' : ''}`}
                        onClick={isMobile ? () => setIsMetaOpen(prev => !prev) : undefined}
                        onKeyDown={isMobile ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setIsMetaOpen(prev => !prev);
                            }
                        } : undefined}
                        role={isMobile ? 'button' : undefined}
                        tabIndex={isMobile ? 0 : undefined}
                        aria-expanded={isMobile ? isMetaOpen : undefined}
                    >
                        <div className="att-top-head-text">
                            <span className={`att-status-badge ${badge.className}`}>{badge.text}</span>
                            <div className="att-title">{headingTitle}</div>
                            {modelInfo?.purpose ?
                                <div className="att-subtitle">{modelInfo.purpose}</div>
                            : ''}
                        </div>
                        {isMobile ?
                            <i className={`bx ${isMetaOpen ? 'bx-caret-up' : 'bx-caret-down'} ds-chevron`} />
                        : ''}
                    </div>

                    {isMobile ?
                        (isMetaOpen ?
                            <div className="att-meta-list">
                                {metaFields.map((field, index) => (
                                    <div key={`meta${index}`} className="att-meta-row">
                                        <span className="att-meta-label">{field.label}</span>
                                        <span className="att-meta-value">{field.value || '-'}</span>
                                    </div>
                                ))}
                            </div>
                        : '')
                    :
                        <FieldBlock>
                            {metaFields.map((field, index) => (
                                <Field key={`meta${index}`} label={field.label} value={field.value} />
                            ))}
                        </FieldBlock>
                    }
                </div>

                <div className="att-top-side att-card">
                    <div className="att-summary-label">{t('attest.toPaySummary')}</div>
                    <div className="att-summary-amount">{ToCustomDecimal(payout)} <span>kr</span></div>

                    <div className="att-summary-divider" />

                    <div className="att-summary-row">
                        <span>{t('attest.orgCost')}</span>
                        <span className="att-summary-row-value">{kr(orgCost)}</span>
                    </div>
                    {employerFee ?
                        <div className="att-summary-row">
                            <span>{t('attest.employerFee')}</span>
                            <span className="att-summary-row-value">{kr(employerFee)}</span>
                        </div>
                    : ''}

                    {canApprove || canDeny ?
                        <div className="att-summary-actions">
                            {actionButtons('top')}
                        </div>
                    : ''}
                </div>
            </div>

            {showHandledByInfo && data?.approvedDeniedByName && (data?.status === "Nekad" || data?.status === "Godkänd" || data?.status === "Utbetald") ?
                <CollapsibleSection title={t('button.info')} collapsible={false}>
                    <FieldBlock>
                        <Field label={t('modelLabel.approvedDeniedByName')} value={data.approvedDeniedByName} />
                        <Field
                            label={t('attest.attested')}
                            value={data.status === "Nekad" ? t('attest.denied') : t('attest.approved')}
                        />
                        <Field label={t('modelLabel.updatedAt')} value={ChangeDateFormat('dateTime', data.updatedAt)} />
                        {data.reason ?
                            <Field label={t('modelLabel.reason')} value={data.reason} wide strong />
                        : ''}
                    </FieldBlock>

                    {data?.status === "Utbetald" && (modelInfo?.recipientAccount || modelInfo?.paidDateTime) ?
                        <FieldBlock>
                            <Field label={t('attest.recipientAccount')} value={modelInfo.recipientAccount} />
                            <Field label={t('attest.paidDateTime')} value={ChangeDateFormat('dateTime', modelInfo.paidDateTime)} />
                        </FieldBlock>
                    : ''}
                </CollapsibleSection>
            : ''}

            {data?.model === "Förrättning" ?
                <AttestReimbursementSections data={modelInfo} />
            : ''}

            {data?.model === "Arvode" ?
                <AttestHonorariumSections data={modelInfo} />
            : ''}

            {data?.model === "Betala" ?
                <AttestPaySections data={modelInfo} />
            : ''}

            {data?.model === "Faktura" ?
                <CollapsibleSection
                    title={t('invoice.invoice')}
                    subtitle={modelInfo?.visualId ? `Id: ${modelInfo.visualId}` : null}
                    collapsible={false}
                >
                    <CardCreditInvoiceInfo dataInvoice={modelInfo} type="Attest"/>
                </CollapsibleSection>
            : ''}

            <AttestLogSection log={data?.log} />

            {isSuperAdmin ?
                <CollapsibleSection title={`${t('modelLabel.id')} (SuperAdmin)`}>
                    <FieldBlock>
                        <Field label={t('modelLabel.id')} value={data?.id} wide />
                        <Field label={t('modelLabel.orgId')} value={data?.orgId} wide />
                    </FieldBlock>
                    <FieldBlock>
                        <Field label={t('modelLabel.modelId')} value={data?.modelId} wide />
                        <Field label={t('modelLabel.createdBy')} value={data?.createdBy} wide />
                        {modelInfo?.userId ?
                            <Field label={t('modelLabel.userId')} value={modelInfo.userId} wide />
                        : ''}
                    </FieldBlock>
                </CollapsibleSection>
            : ''}

            {relatedData?.items?.length >= 1 ?
                <>
                    <div className="att-related-separator" />
                    <CollapsibleSection
                        title={relatedData.items.length === 1 && relatedData.pagination?.totalPages === 1
                            ? t('attest.singleRelatedProceeding')
                            : t('attest.relatedProceedings')}
                    >
                        <RelatedProceedings
                            embedded={true}
                            relatedData={relatedData}
                            data={data}
                            onPageChange={onPageChange}
                            page={page}
                            setPage={setPage}
                            handleClick={openNewTab}
                            sortConfig={sortConfig}
                            onSort={onSort}
                            isLoading={isLoading}
                        />
                    </CollapsibleSection>
                </>
            : ''}

            {showFooterBar ?
            <div className="att-footer-bar">
                <div className="att-footer-info">
                    <span className={`att-status-badge mb-0 ${badge.className}`}>{badge.text}</span>
                    <span className="att-footer-name">{modelVisualId ? `${modelVisualId} - ` : ''}{recipientName}</span>
                    <span className="att-footer-total">
                        {t('attest.toPay')} <span className="att-footer-total-amount">{kr(payout)}</span>
                    </span>
                </div>
                {canApprove || canDeny ?
                    <div className="att-footer-actions">
                        {actionButtons('footer')}
                    </div>
                : ''}
            </div>
            : ''}
        </div>
    );
}
